import type { ApiPrivacyKey, ApiPrivacySettings, PrivacyVisibility } from '../types';

import { tgimFetch } from './httpClient';
import { getTgimAccessToken } from './session';

/**
 * Android `ContactsController.PRIVACY_RULES_TYPE_*` → Web `ApiPrivacyKey`.
 * Bundle `rules` keys are stringified ints (Android/iOS `TgImAccountPrivacyApi`).
 */
const RULE_TYPE_TO_KEY: Record<string, ApiPrivacyKey> = {
  0: 'lastSeen',
  1: 'chatInvite',
  2: 'phoneCall',
  3: 'phoneP2P',
  4: 'profilePhoto',
  5: 'forwards',
  6: 'phoneNumber',
  8: 'voiceMessages',
  9: 'bio',
  11: 'birthday',
  12: 'gifts',
  13: 'noPaidMessages',
};

const KEY_TO_RULE_TYPE: Partial<Record<ApiPrivacyKey, string>> = Object.fromEntries(
  Object.entries(RULE_TYPE_TO_KEY).map(([k, v]) => [v, k]),
);

/** Android `TgImLocalPrivacyStore.TYPE_*`. */
const TYPE_EVERYBODY = 0;
const TYPE_NOBODY = 1;
const TYPE_CONTACTS = 2;

export type TgimPrivacyBundle = {
  rules?: Record<string, number>;
  plus?: Record<string, number[]>;
  minus?: Record<string, number[]>;
  phone_added_by_phone?: number;
  hide_read_marks?: boolean;
  display_gifts_button?: boolean;
  last_seen_privacy?: string;
};

async function authHeaders(): Promise<HeadersInit> {
  const accessToken = await getTgimAccessToken();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

function emptyPrivacy(visibility: PrivacyVisibility = 'everybody'): ApiPrivacySettings {
  return {
    visibility,
    allowUserIds: [],
    allowChatIds: [],
    blockUserIds: [],
    blockChatIds: [],
    botsPrivacy: 'none',
  };
}

function levelToVisibility(level: number): PrivacyVisibility {
  if (level === TYPE_NOBODY) return 'nobody';
  if (level === TYPE_CONTACTS) return 'contacts';
  return 'everybody';
}

function visibilityToLevel(visibility: PrivacyVisibility): number {
  if (visibility === 'nobody') return TYPE_NOBODY;
  if (visibility === 'contacts' || visibility === 'closeFriends') return TYPE_CONTACTS;
  return TYPE_EVERYBODY;
}

function lastSeenStringToVisibility(value?: string): PrivacyVisibility {
  if (value === 'nobody') return 'nobody';
  if (value === 'contacts') return 'contacts';
  return 'everybody';
}

export function mapTgimPrivacyBundleToSettings(
  bundle: TgimPrivacyBundle,
): Partial<Record<ApiPrivacyKey, ApiPrivacySettings>> {
  const out: Partial<Record<ApiPrivacyKey, ApiPrivacySettings>> = {};
  const rules = bundle.rules || {};

  for (const [typeStr, key] of Object.entries(RULE_TYPE_TO_KEY)) {
    const level = rules[typeStr];
    out[key] = emptyPrivacy(
      typeof level === 'number' ? levelToVisibility(level) : 'everybody',
    );
  }

  // Prefer presence string for lastSeen when present.
  if (bundle.last_seen_privacy) {
    out.lastSeen = emptyPrivacy(lastSeenStringToVisibility(bundle.last_seen_privacy));
  }

  // addByPhone uses dedicated field (Android PRIVACY_RULES_TYPE_ADDED_BY_PHONE = 7).
  out.addByPhone = emptyPrivacy(
    typeof bundle.phone_added_by_phone === 'number'
      ? levelToVisibility(bundle.phone_added_by_phone)
      : 'everybody',
  );

  return out;
}

export function applyPrivacyVisibilityToBundle(
  bundle: TgimPrivacyBundle,
  privacyKey: ApiPrivacyKey,
  visibility: PrivacyVisibility,
): TgimPrivacyBundle {
  const next: TgimPrivacyBundle = {
    ...bundle,
    rules: { ...(bundle.rules || {}) },
  };

  if (privacyKey === 'addByPhone') {
    next.phone_added_by_phone = visibilityToLevel(visibility);
    return next;
  }

  if (privacyKey === 'lastSeen') {
    next.last_seen_privacy = visibility === 'nobody'
      ? 'nobody'
      : visibility === 'contacts' || visibility === 'closeFriends'
        ? 'contacts'
        : 'everybody';
  }

  const typeStr = KEY_TO_RULE_TYPE[privacyKey];
  if (typeStr) {
    next.rules![typeStr] = visibilityToLevel(visibility);
  }
  return next;
}

export async function fetchTgimAccountPrivacy(): Promise<TgimPrivacyBundle> {
  return tgimFetch<TgimPrivacyBundle>('/account/privacy', { headers: await authHeaders() });
}

export async function putTgimAccountPrivacy(bundle: TgimPrivacyBundle): Promise<TgimPrivacyBundle> {
  return tgimFetch<TgimPrivacyBundle>('/account/privacy', {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(bundle),
  });
}
